import React from 'react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import { Signatory } from '@interfaces/report-templates.interface';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowMessage } from '@utils/notification';
import { ReportTemplateEndpoints, useDeleteItem } from '@apis/bff-api/report-templates';

export interface DeleteSignatoryDialogProps {
    onClose: () => void;
    signatory: Signatory;
    onDeleteHandler: (success: boolean) => void;
    module: number;
}

const DeleteSignatoryDialog = ({ onClose, signatory, onDeleteHandler, module }: DeleteSignatoryDialogProps) => {
    const { t } = useTranslation();
    const { deleteItem } = useDeleteItem(ReportTemplateEndpoints.DELETE_SIGNATORY);

    const onDelete = async () => {
        try {
            const res = await deleteItem(signatory?.id ?? '', module);
            if (res[0]) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
            onDeleteHandler(res[0]);
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    const characterLength =
        (signatory.firstName ? signatory.firstName.length : 0) +
        (signatory.lastName ? signatory.lastName.length : 0) +
        (signatory.jobTitle ? signatory.jobTitle.length : 0);

    // set the height of the dialogs based on the character length
    const deleteDialogHeight = characterLength > 125 ? 286 : characterLength > 85 ? 272 : 252;
    const signatoryInUseDialogHeight = characterLength > 125 ? 302 : characterLength > 85 ? 275 : 260;

    // used in both dialogs
    const infoFieldMarkup = (
        <>
            <Tooltip position="top" anchorElement="parent" className={cn(style.tooltipWidth)}>
                <section className={cn(style.deleteName)}>
                    <span className="k-font-medium">{t('cm:signatoryName')}:</span>{' '}
                    <span title={`${signatory.firstName} ${signatory.lastName}`}>
                        {signatory.firstName} {signatory.lastName}
                    </span>
                </section>
            </Tooltip>

            <Tooltip position="top" anchorElement="parent" className={cn(style.tooltipWidth)}>
                <section className={cn(style.deleteJobTitle)}>
                    <span className="k-font-medium">{t('reports:jobTitle')}:</span>{' '}
                    <span title={`${signatory.jobTitle}`}>{signatory.jobTitle}</span>
                </section>
            </Tooltip>
        </>
    );

    if (signatory.inUse) {
        return (
            <DraggableDialog
                title={t('reports:signatoryInUse')}
                dataTestId="delete-signatory-in-use-dialog"
                onClose={onClose}
                onSubmit={onClose}
                secondaryBtnLabel=""
                initialHeight={signatoryInUseDialogHeight}
                initialWidth={447}
                primaryBtnLabel={t('common:close')}
            >
                <div className={cn(style.deleteSignatory, 'k-flex k-w-full k-h-full')}>
                    <InfoField type="Info" message={infoFieldMarkup} />

                    <div className={cn(style.deletePromptDiv, 'k-w-12/12 k-text-center')}>
                        <span className={cn(style.signatoryInUsePromptText)}>
                            {t('cm:unableToDeleteSignatoryText')}
                        </span>
                    </div>
                </div>
            </DraggableDialog>
        );
    }

    return (
        <DraggableDialog
            title={`${t('reports:deleteSignatory')}: RP-${signatory?.recordId}`}
            dataTestId="cm2-delete-signatory-dialog"
            onClose={onClose}
            onSubmit={onDelete}
            primaryBtnLabel={t('common:delete')}
            height={deleteDialogHeight}
            width={447}
        >
            <div className={cn(style.deleteSignatory, 'k-flex k-w-full k-h-full')}>
                <div className={cn(style.infoField)}>
                    <InfoField type="Warning" message={infoFieldMarkup} />
                </div>

                <div className={cn(style.deletePromptDiv, 'k-w-12/12 k-text-center')}>
                    <span>{t('reports:confirmDeleteSignatory')}</span>
                </div>
            </div>
        </DraggableDialog>
    );
};

export default DeleteSignatoryDialog;
